import { ref, type Ref } from 'vue'
import { BrowserMultiFormatReader, BarcodeFormat } from '@zxing/browser'
import type { IScannerControls } from '@zxing/browser'
import { Result, DecodeHintType } from '@zxing/library'
import { useToast } from 'primevue/usetoast'

export interface ScannedIdData {
  firstName: string
  lastName: string
  idNumber: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  dateOfBirth?: string
  expirationDate?: string
  issueDate?: string
  // Additional AAMVA fields
  middleName?: string
  sex?: string
  eyeColor?: string
  height?: string
  weight?: string
  hairColor?: string
  placeOfBirth?: string
  vehicleClass?: string
  restrictions?: string
  endorsements?: string
  documentDiscriminator?: string
  country?: string
  nameSuffix?: string
  auditInfo?: string
  inventoryControl?: string
  complianceType?: string
  cardRevisionDate?: string
  hazmatEndorsementExpiration?: string
  limitedDuration?: string
  under18Until?: string
  under19Until?: string
  under21Until?: string
  organDonor?: string
  veteran?: string
  barcodeHash: string
  rawData: string
}

export interface IdScannerOptions {
  onScan?: (data: ScannedIdData) => void
  onError?: (error: string) => void
  videoElement?: Ref<HTMLVideoElement | undefined>
}

export function useIdScanner(options: IdScannerOptions = {}) {
  const _toast = useToast()

  // Scanner state
  const isScanning = ref(false)
  const scannerStatus = ref('Initializing camera...')
  const videoElement = options.videoElement || ref<HTMLVideoElement>()
  const barcodeReader = ref<BrowserMultiFormatReader>()
  const scannerControls = ref<IScannerControls | null>(null)
  const lastScannedHash = ref<string>('')
  const isProcessingScan = ref(false)
  const stopTimeout = ref<number | null>(null)

  // Generate SHA256 hash of the barcode data
  async function generateBarcodeHash(data: string): Promise<string> {
    const encoder = new TextEncoder()
    const dataBuffer = encoder.encode(data)
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  }

  // Parse AAMVA driver's license data
  function parseAAMVALicense(data: string): ScannedIdData | null {
    console.log('Raw barcode data:', data)

    try {
      // Clean up the data - remove any leading/trailing characters
      const cleanData = data.replace(/^[@\s]+/, '')

      // Use manual AAMVA parsing
      let parsedData = parseAAMVAManually(cleanData)

      // If AAMVA parsing fails, try to parse as a simple ID format
      if (!parsedData) {
        console.log('Using simple ID format parsing')
        parsedData = parseSimpleIdFormat(cleanData)
      }

      if (!parsedData) {
        return null
      }

      const result: ScannedIdData = {
        firstName: parsedData.firstName || '',
        lastName: parsedData.lastName || '',
        idNumber: parsedData.idNumber || '',
        address: parsedData.address || '',
        city: parsedData.city || '',
        state: parsedData.state || '',
        postalCode: parsedData.postalCode || '',
        dateOfBirth: parsedData.dateOfBirth || '',
        expirationDate: parsedData.expirationDate || '',
        issueDate: parsedData.issueDate || '',
        // Additional fields
        middleName: parsedData.middleName || '',
        sex: parsedData.sex || '',
        eyeColor: parsedData.eyeColor || '',
        height: parsedData.height || '',
        weight: parsedData.weight || '',
        hairColor: parsedData.hairColor || '',
        placeOfBirth: parsedData.placeOfBirth || '',
        vehicleClass: parsedData.vehicleClass || '',
        restrictions: parsedData.restrictions || '',
        endorsements: parsedData.endorsements || '',
        documentDiscriminator: parsedData.documentDiscriminator || '',
        country: parsedData.country || '',
        nameSuffix: parsedData.nameSuffix || '',
        auditInfo: parsedData.auditInfo || '',
        inventoryControl: parsedData.inventoryControl || '',
        complianceType: parsedData.complianceType || '',
        cardRevisionDate: parsedData.cardRevisionDate || '',
        hazmatEndorsementExpiration: parsedData.hazmatEndorsementExpiration || '',
        limitedDuration: parsedData.limitedDuration || '',
        under18Until: parsedData.under18Until || '',
        under19Until: parsedData.under19Until || '',
        under21Until: parsedData.under21Until || '',
        organDonor: parsedData.organDonor || '',
        veteran: parsedData.veteran || '',
        barcodeHash: '', // Will be set later
        rawData: data,
      }

      console.log('Processed license data:', result)

      // For simple ID formats, we only need an ID number
      if (!result.idNumber) {
        return null
      }

      return result
    } catch (error) {
      console.error('Error parsing AAMVA data:', error)
      return null
    }
  }

  // Manual AAMVA parsing as fallback
  function parseAAMVAManually(data: string): Partial<ScannedIdData> {
    const lines = data.split('\n')
    const result: Partial<ScannedIdData> = {
      firstName: '',
      lastName: '',
      idNumber: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      dateOfBirth: '',
      expirationDate: '',
      issueDate: '',
      // Additional fields
      middleName: '',
      sex: '',
      eyeColor: '',
      height: '',
      weight: '',
      hairColor: '',
      placeOfBirth: '',
      vehicleClass: '',
      restrictions: '',
      endorsements: '',
      documentDiscriminator: '',
      country: '',
      nameSuffix: '',
      auditInfo: '',
      inventoryControl: '',
      complianceType: '',
      cardRevisionDate: '',
      hazmatEndorsementExpiration: '',
      limitedDuration: '',
      under18Until: '',
      under19Until: '',
      under21Until: '',
      organDonor: '',
      veteran: '',
    }

    // Process each line
    for (const line of lines) {
      if (!line.trim()) continue

      // Look for specific AAMVA codes
      if (line.startsWith('DAQ')) {
        result.idNumber = line.substring(3).trim()
      } else if (line.startsWith('DCF')) {
        if (!result.idNumber) {
          const docNumber = line.substring(3).trim()
          const match = docNumber.match(/^[A-Z0-9]+/)
          if (match) {
            result.idNumber = match[0]
          }
        }
      } else if (line.startsWith('DCS')) {
        result.lastName = line.substring(3).trim()
      } else if (line.startsWith('DAC')) {
        result.firstName = line.substring(3).trim()
      } else if (line.startsWith('DAG')) {
        result.address = line.substring(3).trim()
      } else if (line.startsWith('DAI')) {
        result.city = line.substring(3).trim()
      } else if (line.startsWith('DAJ')) {
        result.state = line.substring(3).trim()
      } else if (line.startsWith('DAK')) {
        const postalCode = line.substring(3).trim()
        const zipMatch = postalCode.match(/^(\d{5})(-?\d{4})?/)
        result.postalCode = zipMatch ? zipMatch[0] : postalCode
      } else if (line.startsWith('DBB')) {
        const dob = line.substring(3).trim()
        if (dob.length >= 8) {
          const month = dob.substring(0, 2)
          const day = dob.substring(2, 4)
          const year = dob.substring(4, 8)
          result.dateOfBirth = `${month}/${day}/${year}`
        }
      } else if (line.startsWith('DBA')) {
        const exp = line.substring(3).trim()
        if (exp.length >= 8) {
          const month = exp.substring(0, 2)
          const day = exp.substring(2, 4)
          const year = exp.substring(4, 8)
          result.expirationDate = `${month}/${day}/${year}`
        }
      } else if (line.startsWith('DBD')) {
        const issue = line.substring(3).trim()
        if (issue.length >= 8) {
          const month = issue.substring(0, 2)
          const day = issue.substring(2, 4)
          const year = issue.substring(4, 8)
          result.issueDate = `${month}/${day}/${year}`
        }
      } else if (line.startsWith('DAD')) {
        result.middleName = line.substring(3).trim()
      } else if (line.startsWith('DBC')) {
        result.sex = line.substring(3).trim()
      } else if (line.startsWith('DAY')) {
        result.eyeColor = line.substring(3).trim()
      } else if (line.startsWith('DAU')) {
        result.height = line.substring(3).trim()
      } else if (line.startsWith('DAW')) {
        result.weight = line.substring(3).trim()
      } else if (line.startsWith('DAZ')) {
        result.hairColor = line.substring(3).trim()
      } else if (line.startsWith('DCI')) {
        result.placeOfBirth = line.substring(3).trim()
      } else if (line.startsWith('DCA')) {
        result.vehicleClass = line.substring(3).trim()
      } else if (line.startsWith('DCB')) {
        result.restrictions = line.substring(3).trim()
      } else if (line.startsWith('DCD')) {
        result.endorsements = line.substring(3).trim()
      } else if (line.startsWith('DCF')) {
        result.documentDiscriminator = line.substring(3).trim()
      } else if (line.startsWith('DCG')) {
        result.country = line.substring(3).trim()
      } else if (line.startsWith('DCU')) {
        result.nameSuffix = line.substring(3).trim()
      } else if (line.startsWith('DCJ')) {
        result.auditInfo = line.substring(3).trim()
      } else if (line.startsWith('DCK')) {
        result.inventoryControl = line.substring(3).trim()
      } else if (line.startsWith('DDA')) {
        result.complianceType = line.substring(3).trim()
      } else if (line.startsWith('DDB')) {
        const cardRev = line.substring(3).trim()
        if (cardRev.length >= 8) {
          const month = cardRev.substring(0, 2)
          const day = cardRev.substring(2, 4)
          const year = cardRev.substring(4, 8)
          result.cardRevisionDate = `${month}/${day}/${year}`
        }
      } else if (line.startsWith('DDC')) {
        const hazmat = line.substring(3).trim()
        if (hazmat.length >= 8) {
          const month = hazmat.substring(0, 2)
          const day = hazmat.substring(2, 4)
          const year = hazmat.substring(4, 8)
          result.hazmatEndorsementExpiration = `${month}/${day}/${year}`
        }
      } else if (line.startsWith('DDD')) {
        result.limitedDuration = line.substring(3).trim()
      } else if (line.startsWith('DDH')) {
        const under18 = line.substring(3).trim()
        if (under18.length >= 8) {
          const month = under18.substring(0, 2)
          const day = under18.substring(2, 4)
          const year = under18.substring(4, 8)
          result.under18Until = `${month}/${day}/${year}`
        }
      } else if (line.startsWith('DDI')) {
        const under19 = line.substring(3).trim()
        if (under19.length >= 8) {
          const month = under19.substring(0, 2)
          const day = under19.substring(2, 4)
          const year = under19.substring(4, 8)
          result.under19Until = `${month}/${day}/${year}`
        }
      } else if (line.startsWith('DDJ')) {
        const under21 = line.substring(3).trim()
        if (under21.length >= 8) {
          const month = under21.substring(0, 2)
          const day = under21.substring(2, 4)
          const year = under21.substring(4, 8)
          result.under21Until = `${month}/${day}/${year}`
        }
      } else if (line.startsWith('DDK')) {
        result.organDonor = line.substring(3).trim()
      } else if (line.startsWith('DDL')) {
        result.veteran = line.substring(3).trim()
      }
    }

    // Try to extract license number from DCK field if we still don't have it
    if (!result.idNumber) {
      for (const line of lines) {
        if (line.startsWith('DCK')) {
          const dckData = line.substring(3).trim()
          const match = dckData.match(/^([A-Z0-9]+)/)
          if (match) {
            result.idNumber = match[0]
            break
          }
        }
      }
    }

    return result
  }

  // Parse simple ID format (for non-AAMVA barcodes)
  function parseSimpleIdFormat(data: string): Partial<ScannedIdData> {
    console.log('Attempting to parse as simple ID format:', data)

    // For data like "H082921856OK5TRL01", try to extract meaningful parts
    const result: Partial<ScannedIdData> = {
      firstName: '',
      lastName: '',
      idNumber: data, // Use the entire data as ID number
      address: '',
      city: '',
      state: '',
      postalCode: '',
      dateOfBirth: '',
      expirationDate: '',
      issueDate: '',
    }

    // Try to extract state from the data (e.g., "OK" in "H082921856OK5TRL01")
    const stateMatch = data.match(/[A-Z]{2}/)
    if (stateMatch) {
      result.state = stateMatch[0]
    }

    // Try to extract a date pattern (e.g., "082921" could be MM/DD/YY)
    const dateMatch = data.match(/(\d{2})(\d{2})(\d{2})/)
    if (dateMatch) {
      const month = dateMatch[1]
      const day = dateMatch[2]
      const year = dateMatch[3]
      // Assume 20xx for years
      const fullYear = parseInt(year) < 50 ? `20${year}` : `19${year}`
      result.dateOfBirth = `${month}/${day}/${fullYear}`
    }

    console.log('Simple ID format parsed:', result)
    return result
  }

  // Start scanning
  async function startScanning(): Promise<void> {
    if (isScanning.value) return

    // Reset scan state for new session
    lastScannedHash.value = ''
    isProcessingScan.value = false

    isScanning.value = true
    scannerStatus.value = 'Initializing camera...'

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera access is not supported in this browser or context.')
      }

      // Log device and browser information for debugging
      console.log('Device Info:', {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        vendor: navigator.vendor,
        mediaDevices: !!navigator.mediaDevices,
        getUserMedia: !!navigator.mediaDevices?.getUserMedia,
      })

      // Create hints to focus only on PDF417 format (used on driver's licenses)
      const hints = new Map()
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417])
      hints.set(DecodeHintType.TRY_HARDER, true)

      // Add more hints for better Android compatibility
      hints.set(DecodeHintType.NEED_RESULT_POINT_CALLBACK, true)
      hints.set(DecodeHintType.PURE_BARCODE, false)
      hints.set(DecodeHintType.CHARACTER_SET, 'UTF-8')

      // Create a new code reader with the hints (always create fresh instance)
      if (barcodeReader.value) {
        // Clean up any existing reader
        try {
          if (
            typeof (barcodeReader.value as BrowserMultiFormatReader & { reset?: () => void })
              .reset === 'function'
          ) {
            ;(barcodeReader.value as BrowserMultiFormatReader & { reset?: () => void }).reset?.()
          }
        } catch (e) {
          console.log('Error resetting existing reader:', e)
        }
      }
      barcodeReader.value = new BrowserMultiFormatReader(hints)

      // Check if video element is available
      if (!videoElement.value) {
        console.error('Video element ref:', videoElement)
        console.error('Video element value:', videoElement.value)
        throw new Error(
          'Video element not found. Please ensure the video element is properly rendered.',
        )
      }

      scannerStatus.value = 'Accessing camera...'

      // Check camera access with Android-friendly constraints
      try {
        const constraints = {
          video: {
            facingMode: 'environment', // Use back camera on mobile devices
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 },
            frameRate: { ideal: 30, min: 15 },
          },
        }

        console.log('Requesting camera with constraints:', constraints)
        await navigator.mediaDevices.getUserMedia(constraints)
        console.log('Camera access granted')
      } catch (cameraError) {
        console.error('Camera access error:', cameraError)

        // Try with simpler constraints if the first attempt fails
        try {
          console.log('Trying with simpler constraints...')
          await navigator.mediaDevices.getUserMedia({ video: true })
          console.log('Camera access granted with simple constraints')
        } catch {
          throw new Error(
            `Camera access denied: ${cameraError instanceof Error ? cameraError.message : 'Unknown error'}`,
          )
        }
      }

      // Start decoding from the video device
      console.log('Starting video decoding...')
      await barcodeReader.value.decodeFromVideoDevice(
        undefined, // Use default camera
        videoElement.value,
        async (result, error, controls) => {
          // Save controls so we can stop the loop immediately later
          if (!scannerControls.value && controls) {
            scannerControls.value = controls
          }
          if (result) {
            // Check if we're still supposed to be scanning
            if (!isScanning.value) {
              console.log('Scanner stopped, ignoring result')
              return
            }

            // Check if we're already processing a scan to prevent duplicates
            if (isProcessingScan.value) {
              console.log('Already processing a scan, ignoring duplicate')
              return
            }

            // Generate hash of the barcode text to check for duplicates
            const barcodeText = result.getText()
            const currentHash = await generateBarcodeHash(barcodeText)

            // Check if this is the same barcode we just scanned
            if (lastScannedHash.value === currentHash) {
              console.log('Duplicate barcode detected, ignoring')
              return
            }

            // Successfully scanned a barcode
            scannerStatus.value = 'Barcode detected! Processing...'
            console.log('Barcode detected:', result)
            console.log('Barcode text:', barcodeText)
            console.log('Barcode format:', result.getBarcodeFormat())

            // Log the position where the barcode was detected
            const resultPoints = result.getResultPoints()
            if (resultPoints && resultPoints.length > 0) {
              console.log('Barcode detected at position:', {
                x: resultPoints[0].getX(),
                y: resultPoints[0].getY(),
                area: 'This shows where in the video frame the barcode was found',
              })
            }

            try {
              // Set processing flag to prevent duplicates
              isProcessingScan.value = true
              lastScannedHash.value = currentHash

              // Process the result
              const scannedData = await processScanResult(result)
              if (scannedData && isScanning.value) {
                options.onScan?.(scannedData)
                // Stop the continuous decode loop immediately to avoid extra frames
                try {
                  scannerControls.value?.stop()
                } catch {}
                stopScanning()
              }
            } catch (error) {
              console.error('Error processing scan result:', error)
              options.onError?.(
                error instanceof Error ? error.message : 'Failed to process scan result',
              )
            } finally {
              // Reset processing flag
              isProcessingScan.value = false
            }
          } else if (error) {
            // Handle different types of errors
            if (error.name === 'NotFoundException') {
              scannerStatus.value = 'Searching for barcode... Position ID in the center.'
            } else if (error.name === 'ChecksumException' || error.name === 'FormatException') {
              scannerStatus.value = 'Barcode detected but unreadable. Try adjusting position.'
              console.log('Barcode format error - might be a different format or corrupted data')
            } else if (error.name === 'NotFoundException2') {
              // This is the specific error we're seeing on Android
              console.log(
                'No MultiFormat Readers were able to detect the code. This generally means the barcode is skewed at an angle or the lighting is bad.',
              )
              scannerStatus.value = 'Adjust lighting and position. Try different angles.'
            } else {
              console.error('Scanning error:', error)
              console.log('Error details:', {
                name: error.name,
                message: error.message,
                stack: error.stack,
              })
              scannerStatus.value = 'Keep scanning... Adjust position if needed.'
            }
          }
        },
      )

      scannerStatus.value = 'Ready! Position barcode in the center...'
    } catch (error) {
      console.error('Error initializing barcode scanner:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      scannerStatus.value = `Error initializing scanner: ${errorMessage}`
      options.onError?.(errorMessage)
      stopScanning()
    }
  }

  // Stop scanning
  function stopScanning(): void {
    console.log('Stopping scanner...')

    // Set scanning flag to false immediately to prevent new processing
    isScanning.value = false

    // Stop continuous decoding loop if controls exist
    try {
      scannerControls.value?.stop()
    } catch {}
    scannerControls.value = null

    // First, stop any active video tracks to prevent new frames
    if (videoElement.value && videoElement.value.srcObject) {
      const stream = videoElement.value.srcObject as MediaStream
      stream.getTracks().forEach((track) => {
        track.stop()
        console.log('Video track stopped:', track.kind)
      })
      videoElement.value.srcObject = null
    }

    // Then properly stop the barcode reader
    if (barcodeReader.value) {
      try {
        // Try multiple methods to stop the reader
        const reader = barcodeReader.value as BrowserMultiFormatReader & {
          reset?: () => void
          stopAsyncDecode?: () => void
          stopStreams?: () => void
        }

        if (typeof reader.reset === 'function') {
          reader.reset()
        }

        if (typeof reader.stopAsyncDecode === 'function') {
          reader.stopAsyncDecode()
        }

        // Try to stop any ongoing video decoding
        if (typeof reader.stopStreams === 'function') {
          reader.stopStreams()
        }

        console.log('Barcode reader stopped successfully')
      } catch (e) {
        console.error('Error stopping code reader:', e)
      } finally {
        // Always clear the reference
        barcodeReader.value = undefined
      }
    }

    // Clear the video element completely
    if (videoElement.value) {
      videoElement.value.src = ''
      videoElement.value.srcObject = null
      videoElement.value.load()
      // Pause the video element
      videoElement.value.pause()
    }

    scannerStatus.value = 'Initializing camera...'

    // Reset scan state
    lastScannedHash.value = ''
    isProcessingScan.value = false

    console.log('Scanner stopped completely')

    // Set a timeout to force cleanup if the reader is still running
    if (stopTimeout.value) {
      clearTimeout(stopTimeout.value)
    }
    stopTimeout.value = window.setTimeout(() => {
      if (barcodeReader.value) {
        console.log('Forcing cleanup of barcode reader')
        barcodeReader.value = undefined
      }
      stopTimeout.value = null
    }, 1000) // Force cleanup after 1 second
  }

  // Process scan result
  async function processScanResult(result: Result): Promise<ScannedIdData | null> {
    try {
      const text = result.getText()
      console.log('Barcode content:', text)

      // Parse the AAMVA driver's license data
      const parsedData = parseAAMVALicense(text)
      if (!parsedData) {
        throw new Error('Could not parse license data')
      }

      // Generate hash of the raw barcode data
      const barcodeHash = await generateBarcodeHash(text)
      parsedData.barcodeHash = barcodeHash

      console.log('Processed scan result:', parsedData)
      return parsedData
    } catch (error) {
      console.error('Error processing scan result:', error)
      throw error
    }
  }

  // Cleanup on unmount
  function cleanup(): void {
    if (stopTimeout.value) {
      clearTimeout(stopTimeout.value)
      stopTimeout.value = null
    }
    stopScanning()
  }

  return {
    // State
    isScanning,
    scannerStatus,
    videoElement,

    // Methods
    startScanning,
    stopScanning,
    processScanResult,
    cleanup,
    generateBarcodeHash,
    parseAAMVALicense,
  }
}
